import React from 'react';
import {
    RecentEntitiesModel,
    RecentEntitiesModelAutocompleteEnriched,
} from 'core/services/recent-entities-service/recent-entities-model';
import { getReportEntityClosingDate } from 'core/services/report-entities-service/report-entities-service';
import { AlertRedIcon } from 'components/assets/Icons/Icons';
import { MultilineItemProps, SimpleTooltip } from '@placer-ui/components';
import { Complex } from '@placer-ui/types/src/entities/place';
import { WarningIcon } from 'components/assets/Icons/WarningIcon';
import {
    extractPoiRelevantProperties,
    getPoiIcon,
    getPoiWarningInfo,
    isAddressProviderType,
} from 'features/header/header-top-panel/search-section/components/search-autocomplete/auto-complete-utils/extract-from-poi-utils';

type AutoCompleteSuggestion = {
    data: RecentEntitiesModelAutocompleteEnriched;
    term: string;
    getIsDisabledOption?: (data: RecentEntitiesModel) => boolean;
    flaggedEntityTooltipText?: string;
    hasUnlockNearbyActivity?: boolean;
    hasNearbyPropertyVenueIndicatorFF?: boolean;
    hasRestrictPermission?: boolean;
    hasStoreIdBadgeAutocompleteSearchPermission?: boolean;
};

export interface MultilineItemPropsWithEntity extends MultilineItemProps {
    entity: RecentEntitiesModel;
}

export const convertPoiToAutoCompleteSuggestion = ({
    data,
    flaggedEntityTooltipText,
    hasUnlockNearbyActivity,
    hasNearbyPropertyVenueIndicatorFF,
    hasRestrictPermission,
}: AutoCompleteSuggestion): MultilineItemPropsWithEntity => {
    const poi = data as Complex;
    const closingDate = getReportEntityClosingDate(poi);

    const {
        isPrivacyFlag,
        isRestricted,
        isNearbyProfile,
        noUnlockNearbyActivity,
        poiName,
        poiId,
        flaggedTooltipText,
        location,
    } = extractPoiRelevantProperties({
        poi,
        hasUnlockNearbyActivity,
        hasRestrictPermission,
        flaggedEntityTooltipText,
    });

    const hasAnyWarning =
        isPrivacyFlag ||
        !hasUnlockNearbyActivity ||
        (isNearbyProfile && !!hasNearbyPropertyVenueIndicatorFF) ||
        !!isRestricted;

    const warningInfo = getPoiWarningInfo({
        hasAnyWarning,
        isPrivacyFlag,
        isRestricted,
        isNearbyProfile,
        hasNearbyPropertyVenueIndicatorFF,
        noUnlockNearbyActivity,
        flaggedTooltipText,
        closingDate,
    });

    let labelInfo: string | undefined;
    let RightIcon: React.ReactElement | undefined;
    if (hasAnyWarning) {
        labelInfo = flaggedTooltipText;
        RightIcon = (
            <span>
                <SimpleTooltip isActive title={labelInfo}>
                    <WarningIcon />
                </SimpleTooltip>
            </span>
        );
    } else if (closingDate) {
        labelInfo = warningInfo?.tooltipText;
        RightIcon = (
            <SimpleTooltip title={labelInfo}>
                <AlertRedIcon />
            </SimpleTooltip>
        );
    }

    let finalName = poiName;

    // Locked poi functionality
    const { forceLockIconState, purchased } = data;
    let isLocked;
    if (forceLockIconState) {
        isLocked = forceLockIconState === 'show';
    } else {
        isLocked = !purchased;
    }

    if (isAddressProviderType(poi)) {
        const exactName = poi.address?.formatted_address || poiName;
        finalName = exactName;
    }

    const Icon = getPoiIcon(poi);

    return {
        label: finalName,
        description: location,
        value: poiId,
        Icon,
        RightIcon,
        isDisabled: isPrivacyFlag,
        entity: data,
        labelInfo,
        isLocked,
    };
};
